class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.submitting = false;
    this.events = [
      {
        el: this,
        type: "submit",
        callback: this.submitHandler.bind(this),
      },
      {
        el: this,
        type: "quantity:changed",
        callback: this.quantityHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    handleEventListeners(this.events, "add");
    this.updateTotal();
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  buildItem(formData) {
    const item = {};

    for (const [key, value] of formData.entries()) {
      if (key === "id") item.id = Number(value);
      if (key === "quantity") item.quantity = Number(value);

      if (key.includes("properties")) {
        const propertyKey = key.match(/\[(.*?)\]/)?.[1];
        if (!propertyKey) continue;
        item.properties ||= {};
        item.properties[propertyKey] = value;
      }
    }

    item.quantity = Number.isInteger(item.quantity) && item.quantity > 0 ? item.quantity : 1;
    return item;
  }

  createLineToken(index = 0) {
    const randomValue = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    return `${Date.now()}-${index}-${randomValue}`;
  }

  buildSeparatedItems(item) {
    const quantity = item.quantity;

    return Array.from({ length: quantity }, (_, index) => ({
      ...item,
      quantity: 1,
      properties: {
        ...(item.properties || {}),
        _timestamp: this.createLineToken(index),
      },
    }));
  }

  quantityHandler(e) {
    this.updateTotal(e.detail.quantity);
  }

  updateTotal(quantity = null) {
    const button = this.querySelector(".product-form__button button[data-unit-price]");
    const total = button?.querySelector("[data-product-total]");
    if (!button || !total) return;

    const input = this.form?.querySelector('[name="quantity"]');
    const selectedQuantity = (quantity ?? Number(input?.value)) || 1;
    const unitPrice = Number(button.dataset.unitPrice);
    total.innerHTML = this.formatMoney(unitPrice * selectedQuantity);
  }

  formatMoney(cents) {
    const format = window.theme?.moneyFormat || "£{{ amount }}";
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || "amount";
    const formats = {
      amount: [2, ".", ","],
      amount_no_decimals: [0, ".", ","],
      amount_with_comma_separator: [2, ",", "."],
      amount_no_decimals_with_comma_separator: [0, ",", "."],
      amount_with_apostrophe_separator: [2, ".", "'"],
    };
    const [precision, decimal, thousands] = formats[placeholder] || formats.amount;
    const value = (cents / 100).toFixed(precision);
    const [whole, fraction] = value.split(".");
    const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const formatted =
      fraction && Number(fraction) !== 0
        ? `${formattedWhole}${decimal}${fraction}`
        : formattedWhole;

    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  async submitHandler(e) {
    e.preventDefault();
    if (!this.form?.reportValidity()) return;

    const submitButton = e.submitter;
    const item = this.buildItem(new FormData(this.form));
    const cartLineEditor = this.closest("cart-line-editor");

    if (cartLineEditor) {
      this.dispatchEvent(
        new CustomEvent("cart-line:save", {
          bubbles: true,
          detail: { item, submitButton },
        }),
      );
      return;
    }

    if (this.submitting) return;
    const items = this.buildSeparatedItems(item);
    this.submitting = true;
    if (submitButton) submitButton.disabled = true;
    submitButton?.classList.add("btn--loading");

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error("Something went wrong");
      this.submitting = false;
      if (submitButton) submitButton.disabled = false;
      submitButton?.classList.remove("btn--loading");
      window.location.href = "/cart";
    } catch (error) {
      console.error(error);
      this.submitting = false;
      if (submitButton) submitButton.disabled = false;
      submitButton?.classList.remove("btn--loading");
    }
  }
}

class ProductPropertyForm extends ProductForm {
  constructor() {
    super();
    this.events.push({
      el: this,
      type: "change",
      callback: this.changeHandler.bind(this),
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.syncMeatType();
  }

  changeHandler(e) {
    if (e.target.name === "properties[Meat choice]") this.syncMeatType();
  }

  syncMeatType() {
    const meatChoice = this.querySelector('[name="properties[Meat choice]"]:checked');
    const meatType = this.querySelector("[data-meat-type]");
    if (meatChoice && meatType) meatType.value = meatChoice.value;
  }
}

class StandardProductForm extends ProductPropertyForm {}
class MultiboxBundleProductForm extends ProductPropertyForm {}
class DigitalGiftCardProductForm extends ProductForm {}
class PhysicalGiftCardProductForm extends ProductForm {
  constructor() {
    super();
    this.events.push({
      el: this,
      type: "change",
      callback: this.changeHandler.bind(this),
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.syncGiftSleeve();
  }

  changeHandler(e) {
    if (e.target.matches("[data-gift-sleeve-option]")) this.syncGiftSleeve();
  }

  syncGiftSleeve() {
    const giftSleeveOption = this.querySelector("[data-gift-sleeve-option]:checked");
    const giftSleeve = this.querySelector("[data-gift-sleeve]");
    if (giftSleeveOption && giftSleeve) giftSleeve.value = giftSleeveOption.value;
  }
}

const productForms = {
  "standard-product-form": StandardProductForm,
  "multibox-bundle-product-form": MultiboxBundleProductForm,
  "digital-gift-card-product-form": DigitalGiftCardProductForm,
  "physical-gift-card-product-form": PhysicalGiftCardProductForm,
};

for (const [elementName, ProductFormClass] of Object.entries(productForms)) {
  if (!customElements.get(elementName)) customElements.define(elementName, ProductFormClass);
}
